export type ErrorOrigin = 'server' | 'client';

/**
 * Critical application error that should be caught by error boundaries
 * and reported to monitoring services like Grafana Faro
 */
export class CriticalApplicationError extends Error {
  public readonly origin: ErrorOrigin;
  public readonly timestamp: string;
  public readonly innerError?: Error;

  constructor(message: string, origin: ErrorOrigin, innerError?: Error) {
    super(message);
    this.name = 'CriticalApplicationError';
    this.origin = origin;
    this.timestamp = new Date().toISOString();
    this.innerError = innerError;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CriticalApplicationError);
    }

    // If we have an inner error, preserve its stack
    if (innerError && innerError.stack) {
      this.stack = `${this.stack}\n\nInner Error: ${innerError.stack}`;
    }
  }
}

/**
 * Extension content scripts that inject into an *isolated* world never
 * surface to the host page's error reporting at all — invisible by browser
 * design, nothing to detect. This only catches the two ways an extension
 * DOES become visible to us: a main-world injected `<script>` referencing
 * its own extension:// URL in the stack, or (far more common) an extension
 * mutating the DOM out from under React, which throws when React's
 * reconciler tries to remove/move a node an extension already relocated —
 * a well-documented signature (Grammarly, password managers, ad blockers,
 * translation tools). The third visible case is a main-world injected
 * script (school content filter, managed extension) that wraps
 * `EventTarget.prototype.addEventListener` and calls the native method
 * with the wrong receiver. Chrome then throws `TypeError: Illegal
 * invocation`, and the top stack frame is that injected script
 * (`<anonymous>`), not one of our chunks. Callers underneath (Slate,
 * react-dom attaching an `<input>` listener) are the victims.
 * Heuristic, not certain — a false positive here just means a genuine
 * bug gets mislabeled, not lost; it's still reported either way.
 */
const EXTENSION_PROTOCOL_PATTERN =
  /chrome-extension:\/\/|moz-extension:\/\/|safari-extension:\/\/|safari-web-extension:\/\//i;
const DOM_MUTATION_SIGNATURE_PATTERN =
  /removeChild|insertBefore|is not a child of this node|The object can not be found here/i;

/**
 * Chrome's message when a native DOM method is applied to the wrong
 * receiver. Kept exact so an unrelated error that merely mentions the
 * phrase is not reclassified.
 */
export const ILLEGAL_INVOCATION_MESSAGE = 'Illegal invocation';

export function isIllegalInvocationError(error: unknown): error is Error {
  return error instanceof Error && error.message === ILLEGAL_INVOCATION_MESSAGE;
}

/**
 * V8: "    at n.addEventListener (<anonymous>:1:274)".
 * Firefox/Safari: "n.addEventListener@<anonymous>:1:274".
 * The message line (`TypeError: Illegal invocation`) is not a frame.
 */
function firstStackFrame(stack: string | undefined): string | null {
  if (!stack) return null;
  const lines = stack.split('\n');
  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith('at ')) return trimmed;
    if (i > 0 && trimmed.includes('@')) return trimmed;
  }
  return null;
}

function isInjectedIllegalInvocation(error: Error): boolean {
  if (!isIllegalInvocationError(error)) return false;
  const frame = firstStackFrame(error.stack);
  return frame !== null && frame.includes('<anonymous>');
}

export function isLikelyBrowserExtensionCrash(error: Error): boolean {
  return (
    DOM_MUTATION_SIGNATURE_PATTERN.test(error.message) ||
    EXTENSION_PROTOCOL_PATTERN.test(error.stack ?? '') ||
    isInjectedIllegalInvocation(error)
  );
}

/**
 * webpack throws `ChunkLoadError` ("Loading chunk 866 failed.") when a
 * lazily-loaded JS chunk can't be fetched — almost always a transient network
 * drop (school content filter, flaky mobile/Wi-Fi), since the chunks for the
 * running build are still on the CDN. Safari drops the `name`, so match the
 * message too. CSS chunks use "Loading CSS chunk …".
 */
const CHUNK_LOAD_MESSAGE_PATTERN = /Loading (CSS )?chunk [\w-]+ failed/i;

export function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    CHUNK_LOAD_MESSAGE_PATTERN.test(error.message)
  );
}

const CHUNK_RELOAD_KEY = 'chunkLoadAutoReloadAt';
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

/**
 * True at most once per cooldown window per tab, so a chunk that is really
 * gone (or a network that stays down) shows the fallback screen instead of
 * reload-looping. Records the attempt as a side effect. sessionStorage can
 * throw (privacy mode, blocked storage) — no guard means no auto-reload.
 */
export function claimChunkLoadAutoReload(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (now - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}
